import { useCallback, useEffect, useState } from 'react';
import { CalendarClock, X, Plus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { PAPER } from './theme';

const nameList = (people, max = 3) => {
  const names = people.map(p => p.name).filter(Boolean);
  if (names.length <= max) return names.join(', ');
  return `${names.slice(0, max).join(', ')} +${names.length - max} more`;
};

// Map a schedule-check person to the LabourForm people shape.
const toLabourPerson = (p) => ({
  source: p.user_type === 'sub_contractor' ? 'subcontractor' : 'employee',
  id: p.person_id,
  name: p.name,
});

/**
 * ScheduleCheckBanner — day-level plan-vs-actual nudge on the Site Diary
 * DayView. Flags where the schedule and the diary labour log disagree for
 * THIS job on the viewed day, to encourage both being kept up to date.
 * Flag-only, non-blocking, dismissible for the session (handled by DayView).
 * Admin + surveyor only.
 */
export const ScheduleCheckBanner = ({ projectId, date, dismissed, onDismiss, refreshKey, onLogCrew, onOpenSchedule }) => {
  const { api, isAdmin, isSurveyor } = useAuth();
  const canSee = isAdmin || isSurveyor;
  const [data, setData] = useState(null);

  const fetchCheck = useCallback(async () => {
    if (!canSee || !projectId || !date) return;
    try {
      const res = await api.get(`/diary/projects/${projectId}/schedule-check?date=${date}`);
      setData(res.data);
    } catch (_e) { setData(null); }
  }, [api, projectId, date, canSee]);

  useEffect(() => { fetchCheck(); }, [fetchCheck, refreshKey]);

  if (!canSee || dismissed || !data) return null;
  const lns = data.logged_not_scheduled || [];
  const snl = data.scheduled_not_logged || [];
  if (lns.length === 0 && snl.length === 0) return null;

  return (
    <div
      className="mt-3 rounded-xl px-3.5 py-3"
      style={{ background: '#FEF3C7', border: '1px solid #FCD34D' }}
      data-testid="schedule-check-banner"
    >
      <div className="flex items-start gap-2">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: '#FDE68A', color: '#92400E' }}
        >
          <CalendarClock className="w-4 h-4" strokeWidth={2.25} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: '#92400E' }}>
            Schedule check
          </div>
          <div className="space-y-2.5 mt-1">
            {lns.length > 0 && (
              <div data-testid="schedule-check-logged-not-scheduled">
                <div className="text-[13px] leading-snug" style={{ color: '#78350F' }}>
                  <b>{nameList(lns)}</b> {lns.length === 1 ? 'is' : 'are'} logged here today but not on the schedule for this job — update the schedule?
                </div>
                {onOpenSchedule && (
                  <button
                    type="button"
                    onClick={onOpenSchedule}
                    className="mt-1.5 text-[12px] font-semibold inline-flex items-center gap-1 px-2.5 py-1 rounded-md transition-colors"
                    style={{ background: '#FDE68A', color: '#92400E' }}
                    data-testid="schedule-check-open-schedule-btn"
                  >
                    <CalendarClock className="w-3.5 h-3.5" /> Open schedule
                  </button>
                )}
              </div>
            )}
            {snl.length > 0 && (
              <div data-testid="schedule-check-scheduled-not-logged">
                <div className="text-[13px] leading-snug" style={{ color: '#78350F' }}>
                  <b>{nameList(snl)}</b> {snl.length === 1 ? 'is' : 'are'} scheduled here today but not logged yet — complete the diary, or move them on the schedule if they&apos;re on another job.
                </div>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {onLogCrew && (
                    <button
                      type="button"
                      onClick={() => onLogCrew(snl.map(toLabourPerson))}
                      className="text-[12px] font-semibold inline-flex items-center gap-1 px-2.5 py-1 rounded-md transition-colors"
                      style={{ background: '#92400E', color: '#FEF3C7' }}
                      data-testid="schedule-check-log-crew-btn"
                    >
                      <Plus className="w-3.5 h-3.5" /> Log these people
                    </button>
                  )}
                  {onOpenSchedule && (
                    <button
                      type="button"
                      onClick={onOpenSchedule}
                      className="text-[12px] font-semibold inline-flex items-center gap-1 px-2.5 py-1 rounded-md transition-colors"
                      style={{ background: '#FDE68A', color: '#92400E' }}
                      data-testid="schedule-check-move-schedule-btn"
                    >
                      <CalendarClock className="w-3.5 h-3.5" /> Open schedule
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="p-1 rounded hover:bg-[#FDE68A] flex-shrink-0"
          style={{ color: '#92400E' }}
          title="Dismiss for now"
          data-testid="schedule-check-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ScheduleCheckBanner;
